/**
 * API Details Step (Step 1)
 * Form for entering broker API credentials and account details
 */

import React, { useState, useEffect } from "react";
import { Button, IconButton } from "@mui/material";
import { Icon } from "../../../../components/ds";
import LoadingSpinner from "../../Components/LoadingSpinner";
import AlertMessage from "../../Components/AlertMessage";
import BrokerSelectionGrid from "../Components/BrokerSelectionGrid";

const APIDetailsStep = ({
  isEditMode,
  accountData,
  availableBrokers,
  selectedBroker,
  onBrokerSelect,
  onSubmit,
  onCancel,
  loading,
  appName = "DeeTrade",
  onAppNameChange,
}) => {
  // Helper function to get country flag
  const getCountryFlag = (countryCode) => {
    if (!countryCode) return null;
    const code = countryCode.toLowerCase();
    return `https://flagcdn.com/24x18/${code}.png`;
  };

  // Form state
  const [formData, setFormData] = useState({
    broker_id: "",
    account_holder_name: "",
    app_name: appName,
    api_key: "",
    api_secret: "",
    totp_secret: "",
    additional_params: {},
  });

  // UI state
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [showTotpSecret, setShowTotpSecret] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Initialize form data
  useEffect(() => {
    console.log("APIDetailsStepForm - Effect triggered:", {
      accountData,
      isEditMode,
      appName,
    });

    if (accountData && isEditMode) {
      console.log("Setting form data for edit mode:", accountData);
      setFormData({
        broker_id: accountData.broker_id || "",
        account_holder_name:
          accountData.account_holder_name || accountData.account_holder_name || "",
        app_name: accountData.app_name || appName,
        api_key: accountData.api_key || "",
        api_secret: accountData.api_secret_key || accountData.api_secret || "",
        totp_secret: accountData.totp_secret || "",
        additional_params: accountData.additional_params || {},
      });
    } else if (!isEditMode) {
      // Reset form for new account creation
      setFormData({
        broker_id: selectedBroker?.id || "",
        account_holder_name: "",
        app_name: appName,
        api_key: "",
        api_secret: "",
        totp_secret: "",
        additional_params: {},
      });
    }
  }, [accountData, isEditMode, selectedBroker]);

  // Note: Removed problematic useEffect that was resetting app_name field
  // The app_name field now only gets initialized during the main useEffect above,
  // allowing users to edit the field without it being reset by prop changes.
  // The onAppNameChange callback still keeps parent state in sync when user types.

  // Update broker selection
  useEffect(() => {
    if (selectedBroker) {
      setFormData((prev) => ({
        ...prev,
        broker_id: selectedBroker.id,
      }));
    }
  }, [selectedBroker]);

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Call onAppNameChange when app_name field changes
    if (field === "app_name" && onAppNameChange) {
      onAppNameChange(value);
    }

    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Handle broker selection
  const handleBrokerChange = (event) => {
    const brokerId = event.target.value;
    const broker = availableBrokers.find((b) => b.id === brokerId);
    onBrokerSelect(broker);
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.broker_id) {
      errors.broker_id = "Please select a broker";
    }
    if (!formData.account_holder_name?.trim()) {
      errors.account_holder_name = "Account name is required";
    }
    if (!formData.app_name?.trim()) {
      errors.app_name = "App name is required";
    }
    if (!formData.api_key?.trim()) {
      errors.api_key = "API Key is required";
    }
    if (!formData.api_secret?.trim()) {
      errors.api_secret = "API Secret is required";
    }

    // Broker-specific validation
    if (selectedBroker?.requires_totp && !formData.totp_secret?.trim()) {
      errors.totp_secret = "TOTP Secret is required for this broker";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (event) => {
    event.preventDefault();

    if (validateForm()) {
      onSubmit(formData);
    }
  };

  // Render broker instructions
  const renderBrokerInstructions = () => {
    if (!selectedBroker?.instructions) return null;

    return (
      <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="info" className="text-blue-600 dark:text-blue-400 mt-0.5 text-xl" />
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
              {selectedBroker.name} Setup Instructions
            </h4>
            <p className="text-blue-800 dark:text-blue-200 text-sm">
              {selectedBroker.instructions}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Show different content based on whether a broker is selected */}
      {!selectedBroker && !isEditMode ? (
        /* Broker Selection Grid when no broker is selected */
        <BrokerSelectionGrid
          brokers={availableBrokers}
          onBrokerSelect={onBrokerSelect}
        />
      ) : (
        /* API Details Form when broker is selected or editing */
        <>
          {/* <h2 className="ds-h2 sm: lg: text-fg-1 mb-2 sm:mb-4">
            {isEditMode ? "Update Account Details" : "Enter API Details"}
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-fg-2 mb-4 sm:mb-6 lg:mb-8">
            {isEditMode
              ? "Update your broker connection details. Make sure the credentials are correct."
              : "Enter your broker API credentials to connect your trading account."}
          </p> */}

          {loading ? (
            <LoadingSpinner message="Saving account details..." />
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
              {/* Broker Display (when already selected) */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30 border border-blue-200 dark:border-blue-700 rounded-xl p-4 sm:p-6 lg:p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {/* Country Flag */}
                    {selectedBroker?.country_code && (
                      <img
                        src={getCountryFlag(selectedBroker.country_code)}
                        alt={`${selectedBroker.country} flag`}
                        className="w-6 h-4 object-cover rounded-sm shadow-sm"
                      />
                    )}

                    {/* Broker Logo */}
                    {selectedBroker?.logo && (
                      <img
                        src={selectedBroker.logo}
                        alt={selectedBroker.name}
                        className="w-8 h-8 object-contain rounded-lg bg-white p-1 shadow-sm"
                      />
                    )}

                    {/* Broker Name */}
                    <h3 className="ds-h3 text-fg-1">
                      {selectedBroker?.name || "Broker"}
                    </h3>
                  </div>

                  {/* Change Broker Button */}
                  {!isEditMode && (
                    <button
                      type="button"
                      onClick={() => onBrokerSelect(null)}
                      className="btn btn-secondary btn-sm"
                    >
                      Change Broker
                    </button>
                  )}
                </div>
              </div>

              {/* Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                {/* Account Name */}
                <div className="md:col-span-2 xl:col-span-2">
                  <label className="block text-sm font-medium text-fg-2 mb-2">
                    Account Name
                  </label>
                  <input
                    type="text"
                    value={formData.account_holder_name}
                    onChange={(e) =>
                      handleFieldChange("account_holder_name", e.target.value)
                    }
                    placeholder="e.g., My Primary Trading Account"
                    className={`ds-input ${
                      formErrors.account_holder_name
                        ? "ds-input-error"
                        : ""
                    }`}
                    autoComplete="off"
                    data-form-type="other"
                  />
                  {(formErrors.account_holder_name || !formErrors.account_holder_name) && (
                    <p
                      className={`text-xs mt-1 ${
                        formErrors.account_holder_name
                          ? "text-red-600 dark:text-red-400"
                          : "text-fg-2"
                      }`}
                    >
                      {formErrors.account_holder_name ||
                        "A friendly name for this account"}
                    </p>
                  )}
                </div>

                {/* App Name */}
                <div className="md:col-span-2 xl:col-span-1">
                  <label className="block text-sm font-medium text-fg-2 mb-2">
                    App Name
                  </label>
                  <input
                    type="text"
                    value={formData.app_name}
                    onChange={(e) =>
                      handleFieldChange("app_name", e.target.value)
                    }
                    placeholder="DeeTrade"
                    className={`ds-input ${
                      formErrors.app_name
                        ? "ds-input-error"
                        : ""
                    }`}
                    autoComplete="off"
                    data-form-type="other"
                  />
                  {(formErrors.app_name || !formErrors.app_name) && (
                    <p
                      className={`text-xs mt-1 ${
                        formErrors.app_name
                          ? "text-red-600 dark:text-red-400"
                          : "text-fg-2"
                      }`}
                    >
                      {formErrors.app_name ||
                        "Default is 'DeeTrade', change if broker has naming restrictions"}
                    </p>
                  )}
                </div>

                {/* API Key */}
                <div className="md:col-span-1 xl:col-span-1">
                  <label className="block text-sm font-medium text-fg-2 mb-2">
                    API Key
                  </label>
                  <input
                    type="text"
                    value={formData.api_key}
                    onChange={(e) =>
                      handleFieldChange("api_key", e.target.value)
                    }
                    placeholder="Enter your API Key"
                    className={`ds-input ${
                      formErrors.api_key
                        ? "ds-input-error"
                        : ""
                    }`}
                    autoComplete="off"
                    data-form-type="other"
                  />
                  {(formErrors.api_key || !formErrors.api_key) && (
                    <p
                      className={`text-xs mt-1 ${
                        formErrors.api_key
                          ? "text-red-600 dark:text-red-400"
                          : "text-fg-2"
                      }`}
                    >
                      {formErrors.api_key || "Your broker API key"}
                    </p>
                  )}
                </div>

                {/* API Secret */}
                <div className="md:col-span-1 xl:col-span-1">
                  <label className="block text-sm font-medium text-fg-2 mb-2">
                    API Secret
                  </label>
                  <div className="relative">
                    <input
                      type={showApiSecret ? "text" : "password"}
                      value={formData.api_secret}
                      onChange={(e) =>
                        handleFieldChange("api_secret", e.target.value)
                      }
                      placeholder="Enter your API Secret"
                      className={`ds-input !pr-10 ${
                        formErrors.api_secret
                          ? "ds-input-error"
                          : ""
                      }`}
                      autoComplete="new-password"
                      data-form-type="other"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiSecret(!showApiSecret)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-fg-3 hover:text-fg-2 dark:hover:text-fg-3"
                    >
                      {showApiSecret ? (
                        <Icon name="eye-off" className="h-5 w-5" />
                      ) : (
                        <Icon name="eye" className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {(formErrors.api_secret || !formErrors.api_secret) && (
                    <p
                      className={`text-xs mt-1 ${
                        formErrors.api_secret
                          ? "text-red-600 dark:text-red-400"
                          : "text-fg-2"
                      }`}
                    >
                      {formErrors.api_secret ||
                        "Your broker API secret (kept secure)"}
                    </p>
                  )}
                </div>

                {/* TOTP Secret (if required) - Full width when present */}
                {selectedBroker?.requires_totp && (
                  <div className="md:col-span-2 xl:col-span-1">
                    <label className="block text-sm font-medium text-fg-2 mb-2">
                      TOTP Secret (Required)
                    </label>
                    <div className="relative">
                      <input
                        type={showTotpSecret ? "text" : "password"}
                        value={formData.totp_secret}
                        onChange={(e) =>
                          handleFieldChange("totp_secret", e.target.value)
                        }
                        placeholder="Enter your TOTP Secret"
                        className={`ds-input !pr-10 ${
                          formErrors.totp_secret
                            ? "border-red-500 dark:border-red-400"
                            : "border-[var(--divider)]"
                        }`}
                        autoComplete="new-password"
                        data-form-type="other"
                      />
                      <button
                        type="button"
                        onClick={() => setShowTotpSecret(!showTotpSecret)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-fg-3 hover:text-fg-2 dark:hover:text-fg-3"
                      >
                        {showTotpSecret ? (
                          <Icon name="eye-off" className="h-5 w-5" />
                        ) : (
                          <Icon name="eye" className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                    {(formErrors.totp_secret || !formErrors.totp_secret) && (
                      <p
                        className={`text-xs mt-1 ${
                          formErrors.totp_secret
                            ? "text-red-600 dark:text-red-400"
                            : "text-fg-2"
                        }`}
                      >
                        {formErrors.totp_secret ||
                          "Time-based One-Time Password secret for enhanced security"}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Enhanced Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 border-t border-[var(--divider)]">
                <div className="flex items-center text-sm text-fg-2">
                  <Icon name="info" className="w-4 h-4 mr-2" />
                  Your credentials are encrypted and stored securely
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={onCancel}
                    className="btn btn-secondary btn-lg"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn btn-primary btn-lg"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Saving...
                      </div>
                    ) : isEditMode ? (
                      "Update Details"
                    ) : (
                      "Save & Continue"
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
};

export default React.memo(APIDetailsStep);
